import { useCallback, useEffect, useRef, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { ArrowsClockwise, MagnifyingGlass, PaintBrush, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function ResultsTable({ state }) {
  const rows = Object.entries(state.collections || {}).filter(([, c]) => c.matched > 0).sort((a, b) => b[1].matched - a[1].matched);
  if (!rows.length) return <p className="text-xs text-muted-foreground" data-testid="rebrand-no-changes">Nothing left to change — stored data already reads Fixlr.</p>;
  return (
    <table className="w-full text-xs" data-testid="rebrand-results-table">
      <thead className="text-[10px] uppercase text-muted-foreground">
        <tr className="text-left"><th className="py-1 pr-3">Collection</th><th className="py-1 pr-3 text-right">Docs to change</th><th className="py-1 pr-3 text-right">{state.mode === "apply" ? "Changed" : "—"}</th><th className="py-1">Example</th></tr>
      </thead>
      <tbody>
        {rows.map(([name, c]) => (
          <tr key={name} className="border-t border-border/60 align-top" data-testid={`rebrand-row-${name}`}>
            <td className="py-1 pr-3 font-mono">{name}</td>
            <td className="py-1 pr-3 text-right tabular-nums">{c.matched}</td>
            <td className="py-1 pr-3 text-right tabular-nums">{state.mode === "apply" ? c.changed : ""}</td>
            <td className="py-1 text-muted-foreground">{c.samples?.[0] ? <span><span className="font-mono">{c.samples[0].path}</span>: “{c.samples[0].before}” → “{c.samples[0].after}”</span> : ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function RebrandMigrationCard() {
  const [info, setInfo] = useState(null);
  const [moveMailboxes, setMoveMailboxes] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/platform/settings/rebrand-migration");
      setInfo(data);
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  // Poll while a run is in progress (or right after we started one and the state hasn't flipped to "running" yet)
  useEffect(() => {
    if (info?.state?.status !== "running" && !pending) return undefined;
    timer.current = setTimeout(load, pending ? 600 : 1500);
    return () => clearTimeout(timer.current);
  }, [info, load, pending]);

  // Optimistic "running" until the server reports a run newer than the one we saw when clicking
  useEffect(() => {
    if (pending && info?.state?.started_at && info.state.started_at !== pending) setPending(null);
  }, [info, pending]);

  const start = async (mode) => {
    setBusy(true);
    try {
      await API.post(`/platform/settings/rebrand-migration/${mode}`, { move_mailboxes: moveMailboxes });
      toast.success(mode === "apply" ? "Applying the Fixlr rebrand to stored data…" : "Scanning stored data…");
      setPending(info?.state?.started_at || "none");
      setPendingMode(mode);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start"); } finally { setBusy(false); }
  };

  const state = info?.state || {};
  const running = state.status === "running" || !!pending;
  const shownMode = pending ? pendingMode : state.mode;
  const previewed = !pending && state.status === "done" && state.mode === "preview";
  const applied = !pending && state.status === "done" && state.mode === "apply";
  const total = state.totals?.matched ?? 0;

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="rebrand-migration-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold font-[Chivo] flex items-center gap-2"><PaintBrush size={18} className="text-primary" /> Fixlr rebrand — stored data</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Rewrites the old <strong>Fixli</strong> name, fixli.io / fixli.pro links and old logo filenames inside saved records (platform settings, master workspace, user display names, notifications, activity, invoices, jobs, templates…). Reference numbers like FIXLI-WO-0001, tenant slugs and e-mail addresses are never touched. Preview first — nothing is written until you apply.
          </p>
        </div>
        {state.last_applied_at && <Badge variant="outline" className="shrink-0 text-[10px]" data-testid="rebrand-last-applied">Applied {fmtWhen(state.last_applied_at)} · {state.last_applied_by}</Badge>}
      </div>

      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <Checkbox checked={moveMailboxes} onCheckedChange={(v) => setMoveMailboxes(!!v)} disabled={running} data-testid="rebrand-move-mailboxes" className="mt-0.5" />
        <span>
          Also move platform mailboxes {info?.old_domain ? `@${info.old_domain}` : "@fixli.io"} → {info?.new_domain ? `@${info.new_domain}` : "@fixlr.io"}
          {info?.mailboxes?.length ? ` (${info.mailboxes.join(", ")})` : ""}: sender / reply-to settings and those platform logins. Anyone using one of those logins signs in with the new address afterwards.
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => start("preview")} disabled={busy || running} data-testid="rebrand-preview-btn">
          <MagnifyingGlass size={14} className="mr-1" /> Preview changes
        </Button>
        <Button size="sm" onClick={() => start("apply")} disabled={busy || running || !previewed || total === 0} title={!previewed ? "Run a preview first" : total === 0 ? "Nothing to change" : ""} data-testid="rebrand-apply-btn">
          <PaintBrush size={14} className="mr-1" /> Apply to live data
        </Button>
        {running && <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="rebrand-running"><ArrowsClockwise size={12} className="animate-spin" /> {shownMode === "apply" ? "Applying" : "Scanning"}… {pending ? "starting" : `${state.progress?.done ?? 0}/${state.progress?.total ?? "?"} collections`}</span>}
        {!running && state.status === "done" && (
          <span className="text-xs inline-flex items-center gap-1" data-testid="rebrand-status">
            {state.mode === "apply" ? <CheckCircle size={13} className="text-emerald-600" /> : <MagnifyingGlass size={13} className="text-muted-foreground" />}
            {state.mode === "apply" ? `Applied — ${state.totals?.changed ?? 0} record${(state.totals?.changed ?? 0) === 1 ? "" : "s"} updated` : `Preview — ${total} record${total === 1 ? "" : "s"} would change`}
            {state.totals?.logins ? ` · ${state.totals.logins} login${state.totals.logins === 1 ? "" : "s"} ${state.mode === "apply" ? "moved" : "to move"}` : ""} · {fmtWhen(state.finished_at)}
          </span>
        )}
        {state.status === "failed" && <span className="text-xs text-destructive inline-flex items-center gap-1" data-testid="rebrand-failed"><Warning size={13} /> Failed: {state.errors?.[0]}</span>}
      </div>

      {(previewed || applied) && (
        <div className="space-y-2">
          <ResultsTable state={state} />
          {state.logins?.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="rebrand-logins">
              Logins {state.mode === "apply" ? "moved" : "to move"}: {state.logins.map((l) => `${l.from} → ${l.to}`).join(", ")}
            </p>
          )}
          {state.errors?.length > 0 && <p className="text-xs text-amber-700" data-testid="rebrand-errors">{state.errors.length} collection{state.errors.length === 1 ? "" : "s"} skipped: {state.errors.slice(0, 3).join(" · ")}</p>}
        </div>
      )}
    </div>
  );
}
